import { SelectFieldBinder } from '../../binders/daq';
import { Flux } from '../../flux';
import {
  IGizmoEvent,
  IGizmoFeatures,
  ISingleFeature,
  ISubscription,
} from '../../types';
import Gizmo from '../gizmo';

interface IParams {
  gizmoEvent: IGizmoEvent;
  context: {
    gizmo: Gizmo;
    subscriptions: ISubscription[];
    flux?: Flux;
    binder?: any;
    currentFeatures: IGizmoFeatures;
  };
}

/**
 * Handle the events arriving in the events stream
 *
 * @param {IGizmoEvent} gizmoEvent event arriving from the events stream
 * @returns
 */
function handleGizmoEvents({ gizmoEvent, context }: IParams): Promise<void> {
  return new Promise<void>(async resolve => {
    const { gizmo, subscriptions, flux, binder, currentFeatures } = context;
    const config = gizmo.getConfig();
    // Features we're going to set
    const features: ISingleFeature[] = [];

    if (gizmoEvent.type === 'gizmo.data') {
      const subscription = subscriptions.find(
        s =>
          s.type === 'gizmoData' &&
          ((gizmoEvent.src.gizmoFid !== undefined &&
            s.fid === gizmoEvent.src.gizmoFid) ||
            (gizmoEvent.src.gizmoUid !== undefined &&
              s.uid === gizmoEvent.src.gizmoUid))
      );

      if (subscription) {
        if (
          config.ops?.display?.flux &&
          subscription.uid &&
          config.ops?.display?.flux?.includes(subscription.uid) &&
          flux
        ) {
          const result = await flux.evaluate({
            script: config.ops.display.flux,
          });

          // TODO: setFeatures
          features.push({
            path: 'display',
            value: result,
          });
        }

        if (
          config.ops?.editable?.flux &&
          subscription.uid &&
          config.ops.editable.flux.includes(subscription.uid) &&
          flux
        ) {
          const result = await flux.evaluate({
            script: config.ops.editable.flux,
          });

          features.push({
            path: 'editable',
            value: result,
          });
        }

        if (
          config.ops?.required?.flux &&
          subscription.uid &&
          config.ops.required.flux.includes(subscription.uid) &&
          flux
        ) {
          const result = await flux.evaluate({
            script: config.ops.required.flux,
          });

          features.push({
            path: 'required',
            value: result,
          });
        }

        if (
          config.label.includes('{{') &&
          ((subscription.uid && config.label.includes(subscription.uid)) ||
            (subscription.fid && config.label.includes(subscription.fid))) &&
          flux
        ) {
          const result = await flux.interpolate({
            script: config.label,
          });

          features.push({
            path: 'label',
            value: result,
          });
        }

        // If the 'select_field' is listening for gizmo data via the configured 'gizmoUid' it's
        // going to update the options whenever the gizmo data has been updated
        // This logic is the same for when it's a select_field that has a parent
        if (
          config.type === 'select_field' &&
          (config.ops?.selectField?.gizmoUid ||
            config.ops?.selectField?.gizmoUids) &&
          binder
        ) {
          const selectFieldFinder: SelectFieldBinder = binder as SelectFieldBinder;
          await selectFieldFinder.updateOptions();
        }
        if (
          config.type === 'select_field' &&
          config.ops?.selectField?.parentFieldFid &&
          binder &&
          gizmoEvent.src.gizmoFid === config.ops.selectField.parentFieldFid
        ) {
          const selectFieldFinder: SelectFieldBinder = binder as SelectFieldBinder;
          await selectFieldFinder.updateOptions(
            gizmoEvent.data?.gizmoData?.type
          );
        }

        // TODO: data_table_field

        // Right no we're going to evaluate the flux expressions assigned to value when they don't
        // contain a reference to the user data. This was done because it reassigned the info
        // on all changes. We should create a more elaborate regex to truly filter the unnecessary
        // evaluations.
        const userDataRegex = /{{\s*user\.q[123]?\d?\s*}}/;
        if (
          config.ops?.value?.flux &&
          flux &&
          !userDataRegex.test(config.ops.value.flux)
        ) {
          // If a gizmo.event arrives and the gizmo has configured the value as a flux
          // expression we're going to evaluate the expression and assign the result to the
          // gizmo via the binder
          const result = await flux.evaluate({
            script: config.ops.value.flux,
          });

          // If no condition is set on the value object by default we're going to set it to true so
          // so the arriving value will be assigned
          let conditionResult = true;
          if (config.ops.value.condition) {
            const cr = await flux.evaluate({
              script: config.ops.value.condition,
            });
            if (cr !== undefined) {
              conditionResult = cr;
            }
          }

          if (result !== undefined && binder && conditionResult) {
            if (binder.setValue) {
              binder.setValue(result);
            }
            // TODO: signature_field
          } else if (
            result === undefined &&
            config.ops.value.flux.includes('age(') &&
            binder.setValue
          ) {
            // Right now we're only going to handle the undefined case for the age function
            // TODO: We need to diferentiate between undefined (no value) and null (data was
            // TODO: cleared deliberately)
            await binder.setValue(undefined);
          }
        }
      }
    } else if (
      gizmoEvent.type === 'root-gizmo.operation' &&
      gizmoEvent.data?.rootGizmoOperation?.type ===
        'form.lifecycle.after-submit' &&
      gizmoEvent.dest?.gizmoFid === config.fid &&
      config.type === 'button'
    ) {
      // Right now this condition is used to reset the processing flag on the submit button
      gizmo.setFeatures({
        singleFeatures: [
          {
            path: 'processing',
            value: false,
          },
        ],
      });
    } else if (
      gizmoEvent.type === 'gizmo.set-data' &&
      ((config.uid !== undefined && gizmoEvent.dest?.gizmoUid === config.uid) ||
        (config.fid !== undefined &&
          gizmoEvent.dest?.gizmoFid === config.fid)) &&
      gizmoEvent.data?.gizmoSetData?.value
    ) {
      const binder = gizmo.getBinder();

      if (binder && binder.setValue) {
        await binder.setValue(gizmoEvent.data.gizmoSetData.value);
      }
    } else if (
      gizmoEvent.type === 'gizmo.set-data' &&
      ((config.uid !== undefined && gizmoEvent.dest?.gizmoUid === config.uid) ||
        (config.fid !== undefined &&
          gizmoEvent.dest?.gizmoFid === config.fid)) &&
      gizmoEvent.data?.gizmoSetData?.data
    ) {
      if (binder && binder.setData) {
        await binder.setData({
          data: gizmoEvent.data.gizmoSetData.data,
          event: gizmoEvent,
        });
      }
    } else if (
      gizmoEvent.type === 'gizmo.clear-data' &&
      ((config.uid !== undefined && gizmoEvent.dest?.gizmoUid === config.uid) ||
        (config.fid !== undefined && gizmoEvent.dest?.gizmoFid === config.fid))
    ) {
      if (binder && binder.setValue) {
        await binder.setValue(undefined);
      }
    } else if (
      gizmoEvent.type === 'root-gizmo.operation' &&
      gizmoEvent.data?.rootGizmoOperation?.type ===
        'child.form.lyfecycle.after-submit' &&
      gizmoEvent.dest?.gizmoFid === config.fid
    ) {
      const binder = gizmo.getBinder();

      if (binder && binder.handleChildSubmitEvent) {
        await binder.handleChildSubmitEvent({ event: gizmoEvent });
      }
    } else if (
      gizmoEvent.type === 'root-gizmo.operation' &&
      gizmoEvent.data?.rootGizmoOperation?.type ===
        'child.form.lyfecycle.cancel' &&
      gizmoEvent.dest?.gizmoFid === config.fid
    ) {
      const binder = gizmo.getBinder();

      if (binder && binder.handleChildCancelEvent) {
        await binder.handleChildCancelEvent();
      }
    } else if (
      gizmoEvent.type === 'root-gizmo.operation' &&
      gizmoEvent.data?.rootGizmoOperation?.type === 'form.clear-processing' &&
      currentFeatures.processing === true
    ) {
      features.push({
        path: 'processing',
        value: false,
      });
    } else if (
      gizmoEvent.type === 'gizmo.set-config' &&
      gizmoEvent.data?.gizmoSetConfig?.type === 'update-catalog' &&
      gizmoEvent.dest?.gizmoFid === config.fid &&
      config.type === 'select_field'
    ) {
      const selectFieldBinder = gizmo.getBinder() as SelectFieldBinder;
      await selectFieldBinder.updateOptions(
        gizmoEvent.data.gizmoSetConfig.type
      );
    }

    if (features.length > 0) {
      // If we have feature to set we call the appropiate function
      gizmo.setFeatures({ singleFeatures: features });
    }

    resolve();
  });
}

export default handleGizmoEvents;
